import React, { useContext, useState } from 'react';
import { ButtonProps } from './types';
import { isExtensionInstalled } from '../../utils/tiptap';
import EditorHighlightColor from '@wix/da-ds/pkg/Icons/24x24/EditorHighlightColor';
import { useTranslation } from 'react-i18next';
import EditorIconButton from '../_partials/EditorIconButton';
import { TOOLTIP_HOVER_DELAY } from '../../../constants';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ModalRoot } from '@wix/da-ds/pkg/modals/base';
import PositionOnlyPopperPopover from '@wix/da-ds/pkg/popper/presets/PositionOnlyPopperPopover';
import { OffsetModifier } from '@wix/da-ds/pkg/popper/types';
import ColorPicker from '../../lego/ColorPicker';

import s from './buttons.scss';

const offsetModifier: OffsetModifier = [0, 6];

const FontHighlight: React.FC<ButtonProps> = ({
  editor,
  hideWhenNotEnabled = true,
}) => {
  const { t } = useTranslation('editor');
  const [open, setOpen] = useState(false);
  const isMobile = useContext(MobileContext);

  if (
    !editor.isEditable ||
    (hideWhenNotEnabled && !isExtensionInstalled(editor, 'textHighlight'))
  ) {
    return null;
  }

  const button = (
    <EditorIconButton
      icon={EditorHighlightColor}
      // reusing textAlign here as its the same logic and means we don't need to dupe the tiptap exts
      disabled={!editor.can().chain().focus().setTextAlign('center').run()}
      featureId="format:highlight"
      onClick={() => {
        setOpen(!open);
      }}
      tooltipHoverDelay={TOOLTIP_HOVER_DELAY}
      tooltipText={t('button.fontbackground.tooltip')}
      aria-label={t('button.fontbackground.tooltip')}
    />
  );

  const content = (
    <ColorPicker
      value={editor.getAttributes('highlight').color}
      onChange={value => {
        editor.chain().setHighlight({ color: value }).run();
      }}
      onRemove={() => {
        editor.chain().unsetHighlight().run();
      }}
    />
  );

  if (isMobile) {
    return (
      <>
        {button}
        <ModalRoot
          className={s['color-modal']}
          isFullscreen={false}
          isOpen={open}
          hasCloseButton={false}
          shouldCloseOnOverlayClick
          onRequestClose={() => {
            setOpen(false);
          }}
        >
          {content}
        </ModalRoot>
      </>
    );
  }

  return (
    <PositionOnlyPopperPopover
      popoverContent={content}
      placement="top"
      isVisible={open}
      offsetModifier={offsetModifier}
      onClickOutside={() => {
        setOpen(false);
      }}
    >
      {button}
    </PositionOnlyPopperPopover>
  );
};
FontHighlight.displayName = 'FontHighlight';

export default FontHighlight;
